import CaretDownIcon from '@persona-id/design-tokens/icons/CaretDownIcon';
import { VFC } from 'react';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import NavBack from 'dashboard/components/NavBack';
import Permissioned from 'dashboard/components/Permissioned';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import { Header } from 'dashboard/layouts/IndexViewLayoutV2';
import { Transaction } from 'dashboard/models/transaction';
import { TransactionStatus, TransactionStatusState } from 'dashboard/models/transaction-status';
import { TransactionType } from 'dashboard/models/transaction-type';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu';
import { HStack } from 'design-system/components/Stack';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

interface TransactionsShowHeaderProps {
  transaction: Transaction;
  transactionStatuses: TransactionStatus[];
  transactionType: TransactionType;
  onConfirmRedaction: () => void;
  onSelectNewStatus: (newStatus: TransactionStatus['id']) => void;
}

const TransactionsShowHeader: VFC<TransactionsShowHeaderProps> = ({
  transaction,
  transactionStatuses,
  transactionType,
  onConfirmRedaction,
  onSelectNewStatus,
}) => {
  const [redactModal, { enable: showRedactModal, disable: hideRedactModal }] = useToggle(false);
  const currentTransactionStatus = transactionStatuses.find(
    (transactionStatus) =>
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      transactionStatus.id === transaction.relationships['transaction-status'].data.id,
  );
  const possibleTransactionStatuses = transactionStatuses
    .filter(
      (transactionStatus) =>
        currentTransactionStatus &&
        transactionStatus.id !== currentTransactionStatus.id &&
        transactionStatus.attributes['status'] === TransactionStatusState.Active,
    )
    .map(({ id, attributes: { name: label } }) => ({ id, label }));

  return (
    <Header
      title={transactionType.attributes.name}
      subtitle={transaction.id}
      level='detail'
      prepend={<NavBack to='/dashboard/transactions' aria-label='Back to transactions' />}
    >
      <Permissioned permission={Permission.TransactionWrite} fallback={null}>
        <ModalWrapper isOpen={redactModal} handleCloseModal={hideRedactModal}>
          <ConfirmationModal
            cancelAction={hideRedactModal}
            confirmAction={onConfirmRedaction}
            header='Redact this transaction’s fields?'
            confirmActionText='Redact'
          >
            Personally identifiable information will be redacted. This action is permanent and can’t
            be undone.
          </ConfirmationModal>
        </ModalWrapper>
        <HStack>
          <DropdownMenu
            trigger={
              <Button
                aria-label='Update status of the transaction'
                id='transactions-show-header__status-dropdown-trigger'
                suffixIcon={<CaretDownIcon />}
                variant='basic'
              >
                {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                Status: {currentTransactionStatus.attributes.name}
              </Button>
            }
          >
            {possibleTransactionStatuses.map((status, i) => (
              <DropdownMenu.Item
                id={`transactions-show-header__status-dropdown-${status.id}`}
                key={i}
                onClick={() => onSelectNewStatus(status.id)}
              >
                {status.label}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu>
          <Button
            id='transactions-show-header__redact-button'
            variant='danger'
            onClick={showRedactModal}
          >
            Redact
          </Button>
        </HStack>
      </Permissioned>
    </Header>
  );
};

export default TransactionsShowHeader;
